import { useEffect, useRef, useState } from "react";
import AdSlot from "@pageContent/AdSlot";
import InlinePlayer from "@pageContent/ArticleInlinePlayer/InlinePlayer";
import SocialSharing from "@pageContent/SocialSharing";
import PageContainer from "@pageContent/common/PageContainer";
import CustomVideoError, { CustomErrorType } from "@video/VideoPlayer/CustomVideoError";
import { AD_IDS } from "@constants/consts";
import { Episode } from "@utils/video/dataModels";
import CategoryTabBar from "./CategoryTabBar";
import SeriesTabBar from "./SeriesTabBar";
import * as Styled from "./VideoHeader.styled";

enum TextVisibility {
  initial,
  truncated,
  expanded,
}

const VideoHeader: React.FC<
  | { video?: Episode | null; autoplay?: boolean; isShortFormPage?: false; activeCategorySlug?: undefined }
  | { video?: Video | null; activeCategorySlug?: string; autoplay?: boolean; isShortFormPage: true }
> = ({ video, autoplay = false, isShortFormPage, activeCategorySlug }) => {
  const detailsRef = useRef<HTMLDivElement>(null);
  const descriptionRef = useRef<HTMLParagraphElement>(null);
  const [textVisibility, setTextVisibility] = useState(TextVisibility.initial);
  const [maxHeight, setMaxHeight] = useState<string | number>("auto");

  useEffect(() => {
    const detailsDiv = detailsRef.current;
    const descriptionElement = descriptionRef.current;

    if (!detailsDiv || !descriptionElement) return;

    const offset = 40 + 26 + 20 + 16 + 24 + 40;

    const handleResize = () => {
      setMaxHeight(detailsDiv.offsetHeight - offset);
      const hasTruncatedText = descriptionElement.offsetHeight < descriptionElement.scrollHeight;
      setTextVisibility(hasTruncatedText ? TextVisibility.truncated : TextVisibility.initial);
    };
    handleResize();
    const timeout = setTimeout(handleResize, 100); // Revaluate
    window.addEventListener("resize", handleResize);

    return () => {
      clearTimeout(timeout);
      window.removeEventListener("resize", handleResize);
    };
  }, [detailsRef, descriptionRef, video]);

  if (!video)
    return <Styled.HeaderSkeleton>{isShortFormPage ? <CategoryTabBar /> : <SeriesTabBar />}</Styled.HeaderSkeleton>;

  const { title, videoGuid, imageUrl, description, dateLabel } = video;
  const subtitle = isShortFormPage ? "" : video.subtitle;
  const shareUrl = !isShortFormPage
    ? video.url
    : typeof video.structuredData?.mainEntityOfPage === "object"
    ? video.structuredData.mainEntityOfPage.url
    : video.structuredData?.mainEntityOfPage;

  const isAvailable = isShortFormPage ? true : video.isAvailable;

  return (
    <>
      <Styled.Container isShortFormPage={isShortFormPage}>
        <Styled.Grid>
          <Styled.VideoWrapper>
            {isAvailable ? (
              <InlinePlayer
                title=""
                duration={0}
                imageUrl={imageUrl}
                videoGuid={videoGuid}
                isFullWidthXs
                playerLoadedInitially={autoplay}
              />
            ) : (
              <CustomVideoError errorType={CustomErrorType.NOT_AVAILABLE} />
            )}
          </Styled.VideoWrapper>
          <Styled.DetailsWrapper ref={detailsRef}>
            <Styled.Title>{title}</Styled.Title>
            {subtitle && <Styled.Subtitle>{subtitle}</Styled.Subtitle>}
            {dateLabel && <Styled.AiredDate suppressHydrationWarning>{dateLabel}</Styled.AiredDate>}
            <Styled.DescriptionWrapper maxHeight={maxHeight}>
              <Styled.Description ref={descriptionRef} isExpanded={textVisibility === TextVisibility.expanded}>
                {description}
              </Styled.Description>
              {textVisibility !== TextVisibility.initial && (
                <Styled.ReadMoreButton
                  onClick={() =>
                    setTextVisibility(
                      textVisibility !== TextVisibility.truncated ? TextVisibility.truncated : TextVisibility.expanded
                    )
                  }
                >
                  {textVisibility !== TextVisibility.truncated ? "Lees minder" : "Lees meer"}
                </Styled.ReadMoreButton>
              )}
            </Styled.DescriptionWrapper>
            <SocialSharing url={shareUrl} isVideo />
          </Styled.DetailsWrapper>
        </Styled.Grid>
      </Styled.Container>
      {isShortFormPage ? (
        <>
          <PageContainer>
            <AdSlot id={AD_IDS.contentBanner} />
          </PageContainer>
          <CategoryTabBar activeCategorySlug={activeCategorySlug} />
        </>
      ) : (
        <SeriesTabBar />
      )}
    </>
  );
};

export default VideoHeader;
